import React from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import { MoleculerListResult } from '../../../@sprinx/knihovka-types';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { CatalogueProduct, catalogueViewModeState } from '../../../api/products/catalogue';
import CataloguePageListContentItemCard from './CataloguePageListContentItemCard';
import CataloguePageListContentItemRow from './CataloguePageListContentItemRow';

export type CataloguePageListContentProps = StandardDivProps<CataloguePageListContentClassKey> & {
  catalogue: MoleculerListResult<CatalogueProduct> | undefined;
};

export type CataloguePageListContentClassKey = 'root' | 'cardView' | 'rowView' | 'emptyMessageContainer';

const themeSettings = { name: 'CataloguePageListContent' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CataloguePageListContentClassKey, {}>({
      root: {},
      cardView: {
        display: 'grid',
        columnGap: theme.spacing(2),
        rowGap: theme.spacing(2),
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
        },
        [theme.breakpoints.down('xs')]: {
          gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))',
        },
      },
      rowView: {
        display: 'flex',
        flexWrap: 'wrap',
      },
      emptyMessageContainer: {
        padding: theme.spacing(8, 3),
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      },
    }),
  themeSettings,
);

const CataloguePageListContent = React.forwardRef<HTMLDivElement, CataloguePageListContentProps>(
  ({ className, classes: pClasses, catalogue }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const catalogueView = useRecoilValue(catalogueViewModeState);

    const hasResult = catalogue && catalogue.rows && catalogue.rows.length > 0;

    const ItemComponent = {
      row: CataloguePageListContentItemRow,
      card: CataloguePageListContentItemCard,
    }[catalogueView];

    return (
      <div
        ref={ref}
        className={clsx(
          classes.root,
          {
            [classes.cardView]: hasResult && catalogueView === 'card',
            [classes.rowView]: hasResult && catalogueView === 'row',
            [classes.emptyMessageContainer]: !hasResult,
          },
          className,
        )}
      >
        {hasResult ? (
          catalogue.rows.map((product, idx) => <ItemComponent key={idx.toString()} product={product} />)
        ) : (
          <Alert variant='outlined' severity='warning'>
            {t('cataloguePage/noMatch')}
          </Alert>
        )}
      </div>
    );
  },
);

CataloguePageListContent.displayName = 'CataloguePageListContent';

export default withThemeProps(themeSettings)(CataloguePageListContent);
